import React, { useEffect, useRef, useState } from "react";
import { TCFConstraintsWithoutValues, TCFValueConstriant } from "../types";
import ViewMoreButton from "Components/Common/components/ViewMoreButton";
import { CF_ACTIVATE_MESSAGE } from "../AttributeConstants";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import TextField from "Components/DesignSystem/TextField/TextField";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Search from "Components/DesignSystem/Search/Search";
import Table from "Components/DesignSystem/Table/Table";
import Spinner from "Components/DesignSystem/Spinner/Spinner";

/**
 * The CF constraint form — EditABS's twin without the image/alt columns, on the
 * same DS mapping with the legacy handlers verbatim (synthesized
 * `{ target: { name } }` events, shared index-addressed `attribute-cf-toggle`
 * testid, DS Table value rows, append spinner, 600 ms search debounce).
 */
type EditCFProps = {
    cfDetails: TCFConstraintsWithoutValues | null,
    setCfDetails: React.Dispatch<React.SetStateAction<TCFConstraintsWithoutValues|null>>,
    attributeValuesCount: number,
    fetchCfConstraints: (search?: string, skip?: number, reset?: boolean) => Promise<void>,
    valueCfConstraints: TCFValueConstriant[]
    setValueCfConstraints: React.Dispatch<React.SetStateAction<TCFValueConstriant[]>>
    search?: string
    setSearch: React.Dispatch<React.SetStateAction<string|undefined>>
    hasClickedCFToggle: boolean
    setHasClickedCFToggle: React.Dispatch<React.SetStateAction<boolean>>
    loading?: { status?: string, message?: string }
    /**
     * § 288 — a SEARCH fetch is in flight, as opposed to a view-more append. `loading`
     * above cannot tell them apart: one status covers both, and the field must not
     * claim a search when the user clicked "View More".
     */
    searchLoading?: boolean
}

const SEARCH_DEBOUNCE_MS = 600;

export default function EditCF({
    cfDetails,
    setCfDetails,
    attributeValuesCount,
    fetchCfConstraints,
    valueCfConstraints,
    setValueCfConstraints,
    search,
    setSearch,
    hasClickedCFToggle,
    setHasClickedCFToggle,
    loading,
    searchLoading = false
}: EditCFProps) {
    const VALUE = "value";
    const DISPLAY_VALUE = "displayValue";
    const IMAGE = "image";

    const [cfProp, setCfProp] = useState(cfDetails && cfDetails.cf_properties);

    const [searchInput, setSearchInput] = useState(search ?? "");
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    }, []);
    const handleSearchInput = (value: string) => {
      const trimmed = value.trimStart();
      setSearchInput(trimmed);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => setSearch(trimmed), SEARCH_DEBOUNCE_MS);
    };

    useEffect(() => {
      cfDetails && setCfProp(cfDetails?.cf_properties);
    }, [cfDetails]);

    function getBool(val: string|boolean) {
      return !!JSON.parse(String(val).toLowerCase());
    }

    useEffect(() => {
      if (cfDetails?.cf_properties) {
        if (cfProp?.displaySearch === null || undefined) {
          setCfProp((prevState) => ({
            ...prevState,
            displaySearch: false,
          }));
        }
        if (cfProp?.valuesPerRow === null || undefined) {
          setCfProp((prevState) => ({
            ...prevState,
            valuesPerRow: 1,
          }));
        }
        if (cfProp?.maxHeight === null || undefined) {
          setCfProp((prevState) => ({
            ...prevState,
            maxHeight: "Not Set",
          }));
        }
        if (cfProp?.DisplayType === null || undefined) {
          setCfProp((prevState) => ({
            ...prevState,
            DisplayType: "value",
          }));
        }
      }
      if (cfProp) {
        setCfDetails((prev) => ({
          ...(prev as TCFConstraintsWithoutValues),
          cf_properties: cfProp,
        }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfProp]);
    const handleChange = (e: { target: { name: string } }, value: string|boolean|number) => {
      let field = e.target.name;
      setCfProp((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    };
    const handleChecked = (e: { target: { name: string } }, value: string|boolean, isAbsEnable?: boolean) => {
      let field = e.target.name;
      if(!hasClickedCFToggle) setHasClickedCFToggle(true)
      setCfDetails((prevState) => {
				const nextState = {
						...(prevState as TCFConstraintsWithoutValues),
						[field]: value,
				}

				if(isAbsEnable) nextState['cf_enable_changed'] = true

				return nextState
			});
    };

    useEffect(() => {
      const isAnyCfShowTrue = valueCfConstraints.some(
        (singleValue) => singleValue.cf_show
      );

      if (cfDetails?.cf_enabled) {
        if (!isAnyCfShowTrue) {
          setValueCfConstraints((prev) => {
              const updatedValueConstraints = [...prev].map(
                (singleValue) => {
                  return { ...singleValue, cf_show: true };
                }
            );
            return updatedValueConstraints
          });
        }
      } else {
        if (isAnyCfShowTrue) {
          setValueCfConstraints((prev) => {
              const updatedValueConstraints = [...prev].map(
                (singleValue) => {
                  return { ...singleValue, cf_show: false };
                }
            );
            return updatedValueConstraints
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfDetails?.cf_enabled]);

    function handleValue(e: { target: { name: string } }, value: boolean|string, index: number) {
      let arr = [...valueCfConstraints];
      let obj = { ...arr[index] };
      obj = { ...obj, [e.target.name]: value, changed: true };
      arr.splice(index, 1, obj);
      setValueCfConstraints(arr)
    }

    return (
      <>
        {cfProp && (
          <div className="tw-space-y-4">
            <Toggle
              label={CF_ACTIVATE_MESSAGE}
              data-testid="attribute-cf-toggle"
              checked={getBool(cfDetails?.cf_enabled ?? false)}
              onChange={() =>
                handleChecked({ target: { name: "cf_enabled" } }, !cfDetails?.cf_enabled, true)
              }
            />
            <Toggle
              label="Display Search?"
              data-testid="attribute-cf-toggle"
              disabled={!cfDetails?.cf_enabled}
              checked={getBool(cfProp?.displaySearch ?? false)}
              onChange={() =>
                handleChange(
                  { target: { name: "displaySearch" } },
                  !getBool(cfProp?.displaySearch ?? false)
                )
              }
            />

            <TextField
              label="Display Name"
              size="sm"
              type="text"
              data-testid="componets-editcf-input-1"
              id="floatingInput"
              name="displayName"
              disabled={!cfDetails?.cf_enabled}
              value={cfProp?.displayName ?? ""}
              placeholder="name"
              onChange={(value) =>
                handleChange({ target: { name: "displayName" } }, value)
              }
            />

            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
              <div className="tw-col-span-3">
                <Dropdown
                  label="Display Type"
                  size="sm"
                  data-testid="componets-editcf-select-2"
                  disabled={!cfDetails?.cf_enabled}
                  options={[
                    { value: VALUE, label: "No" },
                    { value: DISPLAY_VALUE, label: "Yes, Text" },
                    { value: IMAGE, label: "Yes, Images" },
                  ]}
                  value={cfProp?.DisplayType}
                  onChange={(value) =>
                    handleChange({ target: { name: "DisplayType" } }, value)
                  }
                />
              </div>
              <div className="tw-col-span-3">
                <Dropdown
                  label="Values Per Row"
                  size="sm"
                  data-testid="componets-editcf-select-3"
                  disabled={!cfDetails?.cf_enabled}
                  options={[
                    { value: 1, label: "One Value" },
                    { value: 2, label: "Two Values" },
                    { value: 3, label: "Three Values" },
                    { value: 4, label: "Four Values" },
                    { value: 5, label: "Five Values" },
                  ]}
                  value={cfProp?.valuesPerRow}
                  onChange={(value) =>
                    handleChange({ target: { name: "valuesPerRow" } }, parseInt(String(value)))
                  }
                />
              </div>
              <div className="tw-col-span-3">
                <Dropdown
                  label="Max Height"
                  size="sm"
                  data-testid="componets-editcf-select-4"
                  disabled={!cfDetails?.cf_enabled}
                  options={[
                    { value: "Not Set", label: "Not Set" },
                    { value: "Small", label: "Small" },
                    { value: "Medium", label: "Medium" },
                    { value: "Large", label: "Large" },
                  ]}
                  value={cfProp?.maxHeight}
                  onChange={(value) =>
                    handleChange({ target: { name: "maxHeight" } }, value)
                  }
                />
              </div>
            </div>

            <Search
              size="sm"
              className="tw-w-60"
              placeholder="Search Attribute Values"
              ariaLabel="Search attribute values"
              data-testid="attribute-cf-search-input"
              value={searchInput}
              onChange={handleSearchInput}
              /* § 288 — the search-only flag (a view-more append must not spin the
                 field), plus the 600ms debounce in front of it. */
              loading={searchLoading || searchInput !== (search ?? "")}
              loadingLabel="Searching values"
            />

            {valueCfConstraints &&
              valueCfConstraints.length > 0 && (
                <>
                  <Table
                    data={valueCfConstraints}
                    rowKey={(row: TCFValueConstriant) => row.value}
                    data-testid="attribute-cf-values-table"
                    columns={[
                      {
                        id: "cf_show",
                        header: "Display",
                        accessor: "cf_show",
                        render: (_value: unknown, singleValue: TCFValueConstriant, index: number) => (
                          <Toggle
                            label=""
                            aria-label={`Show ${singleValue.value}`}
                            data-testid="componets-editcf-input-5"
                            disabled={!cfDetails?.cf_enabled}
                            checked={getBool(singleValue.cf_show)}
                            onChange={() =>
                              handleValue(
                                { target: { name: "cf_show" } },
                                !getBool(singleValue.cf_show),
                                index
                              )
                            }
                          />
                        ),
                      },
                      { id: "value", header: "Value", accessor: "value" },
                      {
                        id: "cf_display_value",
                        header: "CF Display Value",
                        accessor: "cf_display_value",
                        render: (_value: unknown, singleValue: TCFValueConstriant, index: number) => (
                          <TextField
                            label=""
                            size="sm"
                            type="text"
                            data-testid="componets-editcf-input-6"
                            name="cf_display_value"
                            disabled={!cfDetails?.cf_enabled}
                            value={singleValue.cf_display_value ?? ""}
                            placeholder="CF Display value"
                            onChange={(value) =>
                              handleValue({ target: { name: "cf_display_value" } }, value, index)
                            }
                          />
                        ),
                      },
                    ]}
                  />

                  {loading?.status === "Loading" ? (
                    <span
                      className="tw-inline-flex tw-items-center tw-px-2"
                      data-testid="attribute-cf-loading-more"
                    >
                      <Spinner size="sm" label="Loading more values" />
                    </span>
                  ) : (
                    <ViewMoreButton
                      condition = {valueCfConstraints.length < attributeValuesCount}
                      state = {valueCfConstraints.length >= attributeValuesCount}
                      handleClick = {() => fetchCfConstraints(search, valueCfConstraints.length)}
                    />
                  )}
                </>
              )}
          </div>
        )}
      </>
    );
  }
